import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as warehouseActions from '../../actions/warehouse';
import { withRouter } from 'react-router';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaExport, { ExportColumn } from '../../components/MikaExport/MikaExport';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import '../../components/Pagination/Pagination.scss';
import '../Contact/Contact.scss';
import moment from 'moment';

const isMobile = window.innerWidth < 576;

interface Props {
    intl: any;
    loading: boolean;
    history: any;
    warehouse: any;
    warehouseActions: any;
    actions: any;
    permissions: any;
}

const InnerStock: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage }),
        [intl]
    );

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        props.warehouseActions.getInnerStockProducts(currentPage, perPage, searchTerm);
    }, [currentPage, perPage]);

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
        props.warehouseActions.getInnerStockProducts(1, perPage, value);
    };

    const data = props.warehouse?.warehouseInnerStock || [];
    const pagination = props.warehouse?.warehouseInnerStockPagination;

    const columns: MikaColumn[] = useMemo(() => [
        { key: 'designation.value', title: '#', sortable: true, width: '50px', render: (val: any) => <span style={{ color: '#94a3b8', fontSize: '11px' }}>{val}</span> },
        { key: 'designation.label', title: fm('production.board.productCol', 'Produkti'), sortable: true },
        { key: 'clientName',        title: fm('production.detail.client', 'Klienti'), sortable: true },
        { key: 'project.name',      title: fm('production.detail.project', 'Projekti'), hideOnMobile: true },
        { key: 'department.label',  title: fm('production.responsibilityArchive.card.department', 'Reparti'), hideOnMobile: true },
        { key: 'unit',              title: fm('production.detail.quantity', 'Sasia') },
        {
            key: 'finishedProduct',
            title: fm('warehouse.col.completedOn', 'Përfunduar më'),
            sortable: true,
            render: (val: any) => val ? moment(val).format('DD/MM/YYYY HH:mm') : '\u2014'
        },
        {
            key: 'price',
            title: fm('production.detail.value', 'Vlera'),
            hideOnMobile: true,
            render: (val: any) => val ? `${Number(val).toFixed(2)}\u20AC` : '\u2014'
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const tableActions = [
        {
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
            tooltip: fm('common.action.edit', 'Ndrysho'),
            onClick: (row: any) => props.history.push(`/dashboard/warehouse/create/${row.clientID}`)
        }
    ];

    const exportColumns: ExportColumn[] = useMemo(() => [
        { key: 'designation.value', title: '#' },
        { key: 'designation.label', title: fm('production.board.productCol', 'Produkti') },
        { key: 'clientName',        title: fm('production.detail.client', 'Klienti') },
        { key: 'project.name',      title: fm('production.detail.project', 'Projekti') },
        { key: 'department.label',  title: fm('production.responsibilityArchive.card.department', 'Reparti') },
        { key: 'unit',              title: fm('production.detail.quantity', 'Sasia') },
        { key: 'finishedProduct',   title: fm('warehouse.col.completedOn', 'Përfunduar më') },
        { key: 'price',             title: fm('production.detail.value', 'Vlera') },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const fetchAllForExport = async () => data;

    return (
        <div className="clp-page">
            {!isMobile && (
                <MikaPageHeader
                    title={fm('warehouse.strip.innerStock', 'Stoku i brendshëm')}
                    subtitle={pagination ? `${pagination.total} total` : undefined}
                />
            )}

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input"
                        placeholder={fm('warehouse.innerStock.searchPlaceholder', 'Kërko në stok...')}
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => handleSearch('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName="stoku_i_brendshem" title={fm('warehouse.strip.innerStock', 'Stoku i brendshëm')} companyProfile={(props as any).companyProfile} />
                </div>
            </div>

            <MikaTable columns={columns} data={data} actions={tableActions} loading={props.loading} />

            <PaginationComponent
                currentPage={currentPage} perPage={perPage} pagination={pagination}
                onPageChange={(p) => setCurrentPage(p)}
                onPerPageChange={(v) => { setPerPage(v); setCurrentPage(1); }}
                label={fm('warehouse.pagination.label', 'produkte')}
            />
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app, warehouse: state.warehouse };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        warehouseActions: bindActionCreators(warehouseActions as any, dispatch),
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(InnerStock as any)));
